import { useAnalytics } from '@/features/analytics/hooks/use-analytics';
import { RichTextForm } from '@/features/rich-text/components/rich-text-form';
import type { RichTextValue } from '@/features/rich-text/components/rich-text-input';
import { RichTextPlaceholder } from '@/features/rich-text/components/rich-text-placeholder';
import { useLocale } from '@/lib/hooks/use-locale';
import type { CrmEventDTO } from '@/server/dto/crm-event-dto';
import { api } from '@/utils/api';
import { useEffect, useRef, useState } from 'react';

interface EventNoteCreateFormProps {
    event: CrmEventDTO;
    occurrenceDate: string;
    onCancel: () => void;
}

const EventNoteCreateForm = ({ event, occurrenceDate, onCancel }: EventNoteCreateFormProps) => {
    const { t } = useLocale('events');
    const utils = api.useUtils();
    const sendEvent = useAnalytics();

    const createMutation = api.platoUser.command.notes.create.useMutation({
        onSuccess: async () => {
            utils.platoUser.query.events.getEventById.invalidate({ eventId: event.event_id });
            utils.platoUser.query.events.getUpcomingCustomerEvents.invalidate({
                customerId: event.customerId,
            });
            utils.platoUser.query.customerActivity.getFeedList.withFilters.invalidate({
                customerId: event.customerId,
            });
        },
    });

    return (
        <RichTextForm
            autoFocus
            placeholder={t('note_placeholder')}
            submitTitle={t('note_submit_button')}
            entityType="note"
            isSubmitting={createMutation.isLoading}
            onCancel={onCancel}
            onSubmit={(content: RichTextValue, aiSessionId?: string) => {
                createMutation.mutate(
                    {
                        title: content,
                        customerId: event.customerId,
                        eventId: event.event_id,
                        businessCreatedAt: new Date(
                            `${occurrenceDate}T${event.start_time || '12:00:00'}`,
                        ).toISOString(),
                        noteType: 'EVENT',
                    },
                    {
                        onSuccess: (note) => {
                            sendEvent('customer_interaction_created', {
                                note_id: note.noteId,
                                customer_id: note.customerId ?? 'unknown',
                                note_type: note.noteType,
                                ai_session_id: aiSessionId,
                            });
                        },
                    },
                );
            }}
        />
    );
};

export const EventNoteCreate = (props: Omit<EventNoteCreateFormProps, 'onCancel'>) => {
    const { t } = useLocale('events');
    const [isAddingNote, setIsAddingNote] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (isAddingNote) {
            containerRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [isAddingNote]);

    // Separating this two component to avoid unnecessary AI sessions to spawn
    return (
        <div ref={containerRef}>
            {isAddingNote ? (
                <EventNoteCreateForm onCancel={() => setIsAddingNote(false)} {...props} />
            ) : (
                <RichTextPlaceholder
                    onClick={() => setIsAddingNote(true)}
                    placeholderText={t('note_placeholder')}
                />
            )}
        </div>
    );
};
